import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getContrastColor } from '../utils/planningColors';
import { MOTIFS_ABSENCE } from '../utils/absenceMotifs';
import { formatDateFr } from '../utils/planningTime';


// Palette proposée à la création d'un tag. Sept teintes bien séparées : au-delà,
// deux tags deviennent indiscernables d'un coup d'œil, ce qui est précisément ce
// qu'on leur demande de permettre. La saisie libre reste possible à côté.
const COULEURS_TAG = ['#E11D48', '#F59E0B', '#10B981', '#0EA5E9', '#7C3AED', '#EC4899', '#64748B'];

// Motifs d'absence : référentiel partagé (utils/absenceMotifs), aussi consommé
// par le popover de création rapide de la grille du planning.
const MOTIFS = MOTIFS_ABSENCE;

// Les champs d'une absence. Trois écrans les demandent — la fiche du
// collaborateur, l'onglet Absences, la modale d'édition — et une absence reste
// le même objet quel que soit l'endroit d'où on la saisit : un seul formulaire,
// donc, plutôt que trois qui divergent au premier ajout de champ.
function ChampsAbsence({ valeur, onChange, avecRecurrence = false }) {
  const set = (k, v) => onChange({ ...valeur, [k]: v });
  return (
    <>
      <label className="cfg-bloc">
        <span>Du</span>
        <input type="date" value={valeur.date_debut || ''} onChange={e => set('date_debut', e.target.value)} />
      </label>
      <label className="cfg-bloc">
        <span>Au</span>
        <input type="date" value={valeur.date_fin || ''} onChange={e => set('date_fin', e.target.value)} />
        <small>Vide = un seul jour.</small>
      </label>
      <label className="cfg-bloc">
        <span>Motif</span>
        <select value={valeur.motif || 'conges'} onChange={e => set('motif', e.target.value)}>
          {MOTIFS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </label>

      <div className="cfg-bloc cfg-bloc--large">
        <span>Durée</span>
        <div className="cfg-periodes">
          {[['journee', 'Journée'], ['matin', 'Matin'], ['apresmidi', 'Après-midi'], ['creneau', 'Créneau précis']].map(([v, l]) => (
            <button key={v} type="button"
              className={`cfg-periode${(valeur.periode || 'journee') === v ? ' actif' : ''}`}
              onClick={() => set('periode', v)}>{l}</button>
          ))}
        </div>
        {valeur.periode === 'creneau' && (
          <div className="cfg-creneau">
            <input type="time" step="900" value={valeur.heure_debut || ''} onChange={e => set('heure_debut', e.target.value)} />
            <span>→</span>
            <input type="time" step="900" value={valeur.heure_fin || ''} onChange={e => set('heure_fin', e.target.value)} />
          </div>
        )}
      </div>

      {avecRecurrence && (
        <div className="cfg-bloc cfg-bloc--large">
          <label className="cfg-repete">
            <input type="checkbox" checked={!!valeur.repete} onChange={e => set('repete', e.target.checked)} />
            <span>Répéter chaque semaine</span>
          </label>
          {valeur.repete && (
            <div className="cfg-recurrence">
              <div className="cfg-jours">
                {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'].map(j => (
                  <button key={j} type="button"
                    className={`cfg-jour${(valeur.jours || []).includes(j) ? ' actif' : ''}`}
                    onClick={() => set('jours', (valeur.jours || []).includes(j)
                      ? valeur.jours.filter(x => x !== j)
                      : [...(valeur.jours || []), j])}>{j.slice(0, 3).toUpperCase()}</button>
                ))}
              </div>
              <label className="cfg-bloc">
                <span>Jusqu'au</span>
                <input type="date" value={valeur.jusqua || ''} onChange={e => set('jusqua', e.target.value)} />
                <small>Obligatoire — une récurrence sans fin remplirait le planning indéfiniment.</small>
              </label>
            </div>
          )}
        </div>
      )}

      <label className="cfg-bloc cfg-bloc--large">
        <span>Commentaire</span>
        <input value={valeur.commentaire || ''} onChange={e => set('commentaire', e.target.value)} />
      </label>
    </>
  );
}

const JOURS_SEMAINE = [
  { key: 'lundi', label: 'Lun' },
  { key: 'mardi', label: 'Mar' },
  { key: 'mercredi', label: 'Mer' },
  { key: 'jeudi', label: 'Jeu' },
  { key: 'vendredi', label: 'Ven' },
  { key: 'samedi', label: 'Sam' },
];

// Formate une durée décimale (heures) en libellé lisible : 1.5 → "1h30", 0.75 → "45min", 2 → "2h"
const formatDureeHm = (d) => {
  const dec = parseFloat(d) || 0;
  if (dec < 1) return `${Math.round(dec * 60)}min`;
  const h = Math.floor(dec);
  const m = Math.round((dec - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, '0')}`;
};

// Jours d'ouverture des ateliers : numerotation ISO (1 = lundi ... 7 = dimanche),
// la meme qu'en base. A ne pas confondre avec `JOURS_SEMAINE` ci-dessus, qui sert
// aux PAUSES : six jours, clefs textuelles, pas de dimanche.
const JOURS_ISO = [
  { iso: 1, court: 'L', long: 'Lundi' },
  { iso: 2, court: 'M', long: 'Mardi' },
  { iso: 3, court: 'M', long: 'Mercredi' },
  { iso: 4, court: 'J', long: 'Jeudi' },
  { iso: 5, court: 'V', long: 'Vendredi' },
  { iso: 6, court: 'S', long: 'Samedi' },
  { iso: 7, court: 'D', long: 'Dimanche' },
];

// `peut` par défaut PERMISSIF : ce composant peut être monté ailleurs qu'au
// Planning, et un défaut restrictif y retirerait des fonctions existantes.
function PlanningConfig({ concessions, staffList, ateliersList, absencesList: initialAbsences, pausesList, onReload, peut = () => true }) {
  // Chaque onglet de configuration porte sa capacité : masquer l'onglet évite
  // qu'on remplisse un formulaire pour récolter un 403 à l'enregistrement.
  const ONGLETS_CONFIG = [
    ['staff',       '👷 Équipe',          'config.equipe'],
    ['ateliers',    '🏭 Ateliers',        'config.ateliers'],
    ['absences',    '📅 Absences',        'reception.absences'],
    ['pauses',      '☕ Pauses',          'reception.absences'],
    ['prestations', '🔧 Prestations',     'config.rdv_online'],
    ['messages',    '✉️ Messages types',  'config.messages_types'],
    ['documents',   '📄 Documents',       'config.documents_types'],
    ['tags',        '🔖 Tags',            'dossier.tags'],
    ['acces',       '🔐 Gestion des accès', 'config.gestion_acces'],
  ];
  const [tab, setTab] = useState('staff');
  const [concessionId, setConcessionId] = useState('');

  // Prestations en ligne
  const [prestationsOnline, setPrestationsOnline] = useState([]);
  const [showPrestForm, setShowPrestForm] = useState(false);
  const [editPrest, setEditPrest] = useState(null);
  const [prestForm, setPrestForm] = useState({
    role_rapide: 'none', role_meca: 'none', categorie: '', libelle: '', duree: 1, icone: '', visible_planning: true, concession_id: ''
  });

  // Messages types (SMS / e-mail), configurables par concession
  const [messages, setMessages] = useState([]);
  const [messageVars, setMessageVars] = useState([]);
  const [showMsgForm, setShowMsgForm] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [msgForm, setMsgForm] = useState({
    libelle: '', canal: 'sms', objet: '', contenu: '', ordre: 0, actif: true, concession_id: ''
  });

  // Gestion des accès : rôles Planning par compte (technicien / administrateur)
  const [acces, setAcces] = useState([]);
  const [accesErreur, setAccesErreur] = useState('');
  // Les rôles attribuables viennent du serveur : aucun code en dur ici, un 6ᵉ
  // rôle apparaîtra dans le sélecteur sans toucher au frontend.
  const [rolesPlanning, setRolesPlanning] = useState([]);
  const chargerAcces = () => api.get('/planning/acces')
    .then(res => setAcces(res.data.acces || []))
    .catch(e => setAccesErreur(e.response?.data?.error || 'Chargement impossible'));
  useEffect(() => {
    if (tab !== 'acces') return;
    setAccesErreur('');
    chargerAcces();
    api.get('/planning/roles').then(r => setRolesPlanning(r.data.roles || [])).catch(() => setRolesPlanning([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  const changerRole = async (userId, role) => {
    // Optimiste : le select reflète le choix immédiatement, rollback si le
    // serveur refuse.
    const avant = acces;
    setAcces(a => a.map(x => x.user_id === userId ? { ...x, role_planning: role } : x));
    try {
      await api.post('/planning/acces', { user_id: userId, role });
    } catch (e) {
      setAcces(avant);
      setAccesErreur(e.response?.data?.error || 'Enregistrement impossible');
    }
  };

  const chargerMessages = () => api.get('/planning/messages-types')
    .then(res => { setMessages(res.data.messages || []); setMessageVars(res.data.variables || []); })
    .catch(() => {});

  useEffect(() => {
    if (tab === 'messages') chargerMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, concessionId]);

  // Tags d'intervention, configurables par concession
  const [tags, setTags] = useState([]);
  const [showTagForm, setShowTagForm] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [tagForm, setTagForm] = useState({ nom: '', couleur: COULEURS_TAG[0], ordre: 0, actif: true, concession_id: '' });
  const [tagErreur, setTagErreur] = useState('');

  const chargerTags = () => api.get('/planning/tags')
    .then(res => setTags(res.data.tags || []))
    .catch(() => {});

  useEffect(() => {
    if (tab === 'tags') chargerTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, concessionId]);

  const saveTag = async () => {
    setTagErreur('');
    try {
      const payload = { ...tagForm, concession_id: tagForm.concession_id || concessionId || undefined };
      if (editTag) await api.put(`/planning/tags/${editTag.id}`, payload);
      else await api.post('/planning/tags', payload);
      setShowTagForm(false); setEditTag(null);
      await chargerTags();
      if (onReload) onReload();
    } catch (e) {
      // Le doublon de nom (409) est une erreur de saisie : elle s'affiche DANS le
      // formulaire, sans le refermer, pour qu'on puisse corriger le nom.
      setTagErreur(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  // Un tag ne se supprime pas, il se désactive : les dossiers gardent leur
  // liaison, et le réactiver la fait réapparaître. On le dit explicitement quand
  // des dossiers sont concernés.
  const basculerTagActif = async (t) => {
    if (t.actif && t.nb_dossiers > 0
      && !window.confirm(`« ${t.nom} » est posé sur ${t.nb_dossiers} dossier(s).\n\nLe désactiver le retire du menu et du filtre, mais ne l'efface pas : le réactiver le fera réapparaître sur les mêmes dossiers.\n\nContinuer ?`)) return;
    try { await api.put(`/planning/tags/${t.id}`, { actif: !t.actif }); await chargerTags(); if (onReload) onReload(); }
    catch (e) { alert(e.response?.data?.error || 'Modification impossible'); }
  };

  const tagsAffiches = concessionId ? tags.filter(t => String(t.concession_id) === String(concessionId)) : tags;

  const saveMsg = async () => {
    try {
      const payload = { ...msgForm, concession_id: msgForm.concession_id || concessionId || undefined };
      if (editMsg) await api.put(`/planning/messages-types/${editMsg.id}`, payload);
      else await api.post('/planning/messages-types', payload);
      setShowMsgForm(false); setEditMsg(null);
      await chargerMessages();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Supprimer ce message type ?')) return;
    try { await api.delete(`/planning/messages-types/${id}`); await chargerMessages(); }
    catch { alert('Suppression impossible'); }
  };

  // ── Documents types imprimables — patron identique aux messages types ──────
  const [docs, setDocs] = useState([]);
  const [docVars, setDocVars] = useState([]);
  const [showDocForm, setShowDocForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [docForm, setDocForm] = useState({ libelle: '', contenu: '', ordre: 0, actif: true, concession_id: '' });
  // Aperçu : rendu du contenu EN COURS D'ÉDITION sur le dossier le plus récent
  // de la concession — l'admin voit ce que le client verra, avant d'enregistrer.
  const [docApercuHtml, setDocApercuHtml] = useState(null);

  const chargerDocs = () => api.get('/planning/documents-types')
    .then(res => { setDocs(res.data.documents || []); setDocVars(res.data.variables || []); })
    .catch(() => {});

  useEffect(() => {
    if (tab === 'documents') chargerDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, concessionId]);

  const saveDoc = async () => {
    try {
      const payload = { ...docForm, concession_id: docForm.concession_id || concessionId || undefined };
      if (editDoc) await api.put(`/planning/documents-types/${editDoc.id}`, payload);
      else await api.post('/planning/documents-types', payload);
      setShowDocForm(false); setEditDoc(null); setDocApercuHtml(null);
      await chargerDocs();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm('Supprimer ce document type ?')) return;
    try { await api.delete(`/planning/documents-types/${id}`); await chargerDocs(); }
    catch { alert('Suppression impossible'); }
  };

  const apercuDoc = async () => {
    try {
      const { data } = await api.post('/planning/documents-types/apercu', {
        contenu: docForm.contenu,
        concession_id: docForm.concession_id || concessionId || undefined,
      });
      setDocApercuHtml(data.html);
    } catch (e) {
      alert(e.response?.data?.error || 'Aperçu impossible');
    }
  };

  useEffect(() => {
    if (tab === 'prestations') {
      api.get('/planning/prestations-online')
        .then(res => setPrestationsOnline(res.data || []))
        .catch(() => {});
    }
  }, [tab]);

  const savePrest = async () => {
    try {
      const payload = { ...prestForm, concession_id: prestForm.concession_id || concessionId || undefined };
      if (editPrest) {
        await api.put(`/planning/prestations-online/${editPrest.id}`, payload);
      } else {
        await api.post('/planning/prestations-online', payload);
      }
      setShowPrestForm(false);
      setEditPrest(null);
      const res = await api.get('/planning/prestations-online');
      setPrestationsOnline(res.data || []);
      // Rafraîchir la liste travauxTypes + autres données côté PlanningAtelier
      // pour que la nouvelle prestation soit proposée dans le formulaire de création de RDV
      if (onReload) onReload();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const deletePrest = async (id) => {
    if (!window.confirm('Désactiver cette prestation ?')) return;
    await api.delete(`/planning/prestations-online/${id}`);
    const res = await api.get('/planning/prestations-online');
    setPrestationsOnline(res.data || []);
    if (onReload) onReload();
  };

  // Badge de rôle d'une prestation dans un service (côté client).
  const roleBadge = (role) => {
    const base = { padding: '2px 8px', borderRadius: '999px', fontSize: '11.5px', fontWeight: 600 };
    if (role === 'principale') return <span style={{ ...base, background: 'var(--color-success-light)', color: 'var(--color-success-dark)' }}>Principale</span>;
    if (role === 'complement') return <span style={{ ...base, background: 'var(--color-info-light)', color: 'var(--color-info-dark)' }}>Complément</span>;
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  };
  const [absences, setAbsences] = useState([]);

  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({ nom: '', prenom: '', role: 'mecanicien', atelier_id: '', manager: '', specialite: '', couleur: '#6366f1', concession_id: '', nextlane_rcp_ids: {}, actif: true, ordre: 0, date_entree: '', date_sortie: '' });

  const [showAtelierForm, setShowAtelierForm] = useState(false);
  const [editAtelierId, setEditAtelierId] = useState(null);
  const [atelierForm, setAtelierForm] = useState({ nom: '', concession_id: '', heure_ouverture: '07:00', heure_fermeture: '17:00', jours_ouverture: [1, 2, 3, 4, 5, 6], ordre: '' });


  const [showPauseForm, setShowPauseForm] = useState(false);
  const [editPause, setEditPause] = useState(null);
  const defaultPauseForm = { concession_id: '', atelier_id: '', heure_debut: '12:00', heure_fin: '13:00', libelle: 'Pause déjeuner', lundi: true, mardi: true, mercredi: true, jeudi: true, vendredi: true, samedi: false };
  const [pauseForm, setPauseForm] = useState({ ...defaultPauseForm });

  // Correspondance réceptionnaire → code RCP Datacar. Chargée à la demande, et
  // uniquement pour un réceptionnaire : les codes RCP sont propres à chaque
  // dealership (une concession peut en porter deux, ex. SOGUAVA + SOREC, où le
  // même agent n'a pas le même code), donc une liste par dealership.
  const [rcpDealerships, setRcpDealerships] = useState([]);
  const [rcpOptions, setRcpOptions] = useState({});
  const [rcpLoading, setRcpLoading] = useState(false);
  const [rcpErreur, setRcpErreur] = useState('');

  useEffect(() => {
    if (!showStaffForm || staffForm.role !== 'receptionnaire' || !staffForm.concession_id) {
      setRcpDealerships([]); setRcpOptions({}); setRcpErreur('');
      return;
    }
    let annule = false;
    (async () => {
      setRcpLoading(true); setRcpErreur('');
      try {
        const { data: dealers } = await api.get('/nextlane/concessions', { params: { concession_id: staffForm.concession_id } });
        if (annule) return;
        setRcpDealerships(dealers || []);
        const listes = await Promise.all((dealers || []).map(async (d) => {
          try {
            const { data } = await api.get(`/nextlane-appointments/standard-codes/${d.id}`);
            return [d.id, data?.RCP || []];
          } catch { return [d.id, []]; }
        }));
        if (annule) return;
        setRcpOptions(Object.fromEntries(listes));
      } catch (e) {
        if (!annule) setRcpErreur("Codes réceptionnaires Datacar indisponibles — le mappage existant est conservé.");
      } finally {
        if (!annule) setRcpLoading(false);
      }
    })();
    return () => { annule = true; };
  }, [showStaffForm, staffForm.role, staffForm.concession_id]);

  useEffect(() => {
    loadAbsences();
  }, [concessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAbsences = async () => {
    try {
      const { data } = await api.get('/planning/absences');
      setAbsences(data);
    } catch (e) { console.error('[planning/absences load]', e); }
  };

  // ⚠️ La grille du planning ne reçoit que les collaborateurs ACTIFS. L'écran de
  // configuration, lui, doit voir les inactifs — sinon la case « Actif » serait
  // une porte à sens unique : la personne disparaîtrait de la liste où se trouve
  // le bouton qui permet de la réactiver.
  const [staffTous, setStaffTous] = useState(null);
  const chargerStaffComplet = useCallback(() => {
    api.get('/planning/staff', { params: { inclure_inactifs: 1 } })
      .then(r => setStaffTous(Array.isArray(r.data) ? r.data : (r.data?.staff || [])))
      .catch(() => setStaffTous(null));   // repli silencieux sur la liste reçue en prop
  }, []);
  useEffect(() => { chargerStaffComplet(); }, [chargerStaffComplet]);
  const staffEffectif = staffTous || staffList;

  const filteredStaff = concessionId ? staffEffectif.filter(s => String(s.concession_id) === String(concessionId)) : staffEffectif;
  const filteredAteliers = concessionId ? ateliersList.filter(a => String(a.concession_id) === String(concessionId)) : ateliersList;
  const filteredAbsences = concessionId
    ? absences.filter(a => {
        const staff = staffList.find(s => s.id === a.staff_id);
        return staff && String(staff.concession_id) === String(concessionId);
      })
    : absences;

  const saveStaff = async () => {
    try {
      const payload = { ...staffForm, concession_id: staffForm.concession_id || null, atelier_id: staffForm.atelier_id || null };
      if (editStaff) {
        await api.put(`/planning/staff/${editStaff.id}`, payload);
      } else {
        await api.post('/planning/staff', payload);
      }
      setShowStaffForm(false);
      setEditStaff(null);
      chargerStaffComplet();
      setStaffForm({ nom: '', prenom: '', role: 'mecanicien', atelier_id: '', manager: '', specialite: '', couleur: '#6366f1', concession_id: concessionId || '', nextlane_rcp_ids: {}, actif: true, ordre: 0, date_entree: '', date_sortie: '' });
      onReload();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const deleteStaff = async (id) => {
    if (!window.confirm('Désactiver ce collaborateur ?')) return;
    await api.delete(`/planning/staff/${id}`);
    onReload();
  };

  const saveAtelier = async () => {
    try {
      const payload = {
        ...atelierForm,
        concession_id: atelierForm.concession_id || null,
        heure_ouverture: atelierForm.heure_ouverture || '07:00',
        heure_fermeture: atelierForm.heure_fermeture || '17:00',
      };
      // Champ vide = « range-le toi-meme » : on retire la clef plutot que
      // d'envoyer une chaine vide, que le serveur lirait comme « inchange ».
      if (payload.ordre === '' || payload.ordre === null) delete payload.ordre;
      if (editAtelierId) {
        await api.put(`/planning/ateliers/${editAtelierId}`, payload);
      } else {
        await api.post('/planning/ateliers', payload);
      }
      setShowAtelierForm(false);
      setEditAtelierId(null);
      setAtelierForm({ nom: '', concession_id: concessionId || '', heure_ouverture: '07:00', heure_fermeture: '17:00', jours_ouverture: [1, 2, 3, 4, 5, 6], ordre: '' });
      onReload();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const editAtelier = (a) => {
    setEditAtelierId(a.id);
    setAtelierForm({
      nom: a.nom || '',
      concession_id: a.concession_id || '',
      jours_ouverture: (a.jours_ouverture && a.jours_ouverture.length ? a.jours_ouverture.map(Number) : [1, 2, 3, 4, 5, 6]),
      ordre: a.ordre ?? '',
      heure_ouverture: (a.heure_ouverture || '07:00').slice(0, 5),
      heure_fermeture: (a.heure_fermeture || '17:00').slice(0, 5),
    });
    setShowAtelierForm(true);
  };

  const deleteAtelier = async (id) => {
    if (!window.confirm('Supprimer cet atelier ?')) return;
    await api.delete(`/planning/ateliers/${id}`);
    onReload();
  };

  // ⚠️ Sans try/catch, le refus 403 partait en rejet non capturé : la ligne
  // restait affichée, aucun message, et l'utilisateur recliquait (constaté en
  // production le 2026-08-26 : 7 refus muets sur la même absence en 90 secondes).
  // Le verrou est côté serveur — DELETE exige le droit « supprimer », que seuls
  // 11 comptes sur 47 possèdent.
  const deleteAbsence = async (id) => {
    if (!window.confirm('Supprimer cette absence ?')) return;
    try {
      await api.delete(`/planning/absences/${id}`);
    } catch (e) {
      alert(e.response?.status === 403
        ? "Vous n'avez pas le droit de supprimer une absence. Demandez à un administrateur."
        : (e.response?.data?.error || 'Suppression impossible.'));
      return;
    }
    loadAbsences();
    chargerAbsencesDe(editStaff?.id);
  };

  // ─── Modale d'édition d'une absence ───────────────────────────────────────
  // Partagée par l'onglet Absences et la fiche du collaborateur : c'est le même
  // objet, il n'y a aucune raison de le modifier de deux façons différentes.
  const [absenceEditee, setAbsenceEditee] = useState(null);

  const enregistrerAbsenceEditee = async () => {
    if (!absenceEditee?.date_debut) { alert('Indiquez au moins une date de début.'); return; }
    if (!absenceEditee.id && !absenceEditee.staff_id) { alert('Choisissez un collaborateur.'); return; }
    try {
      const [hd, hf] = bornesPeriode(absenceEditee, absenceEditee.staff_id);
      const corps = {
        date_debut: absenceEditee.date_debut,
        date_fin: absenceEditee.date_fin || absenceEditee.date_debut,
        motif: absenceEditee.motif,
        commentaire: absenceEditee.commentaire || null,
        heure_debut: hd, heure_fin: hf,
      };
      if (absenceEditee.id) {
        await api.put(`/planning/absences/${absenceEditee.id}`, corps);
      } else {
        const r = await api.post('/planning/absences', {
          ...corps,
          staff_id: absenceEditee.staff_id,
          recurrence: absenceEditee.repete
            ? { jours: absenceEditee.jours, jusqua: absenceEditee.jusqua }
            : undefined,
        });
        if (r.data?.occurrences) {
          alert(`${r.data.occurrences} absences créées, du ${formatDateFr(r.data.de)} au ${formatDateFr(r.data.a)}.`);
        }
      }
    } catch (e) {
      alert(e.response?.status === 403
        ? "Vous n'avez pas le droit de modifier une absence. Demandez à un administrateur."
        : (e.response?.data?.error || 'Enregistrement impossible.'));
      return;
    }
    setAbsenceEditee(null);
    loadAbsences();
    chargerAbsencesDe(editStaff?.id);
  };

  // Une série est un lot d'occurrences : les retirer une par une serait absurde
  // sur une récurrence hebdomadaire qui peut compter des dizaines de lignes.
  const supprimerSerie = async (serieId) => {
    if (!window.confirm('Supprimer toutes les occurrences de cette absence récurrente ?')) return;
    try {
      const r = await api.delete(`/planning/absences/serie/${serieId}`);
      alert(`${r.data?.supprimees ?? 0} occurrences supprimées.`);
    } catch (e) {
      alert(e.response?.status === 403
        ? "Vous n'avez pas le droit de supprimer une absence. Demandez à un administrateur."
        : (e.response?.data?.error || 'Suppression impossible.'));
      return;
    }
    setAbsenceEditee(null);
    loadAbsences();
    chargerAbsencesDe(editStaff?.id);
  };

  const ouvrirAbsence = (a) => setAbsenceEditee({
    id: a.id,
    staff_id: a.staff_id,
    staff_nom: a.staff_nom || `${a.prenom || ''} ${a.nom || ''}`.trim(),
    date_debut: (a.date_debut || '').slice(0, 10),
    date_fin: (a.date_fin || '').slice(0, 10),
    motif: a.motif || 'conges',
    commentaire: a.commentaire || '',
    // Rouvrir une absence partielle en « journée entière » l'élargirait en
    // silence : la période se relit depuis les bornes stockées.
    periode: a.heure_debut ? 'creneau' : 'journee',
    heure_debut: (a.heure_debut || '').slice(0, 5),
    heure_fin: (a.heure_fin || '').slice(0, 5),
    serie_id: a.serie_id || null,
  });

  // ─── Absences DU collaborateur ouvert dans la fiche ────────────────────────
  // Même API, filtrée par personne : l'historique se lit là où on le cherche,
  // sans passer par la liste globale de l'onglet Absences — qui garde son rôle
  // de vue transverse (« qui est absent cette semaine ? »).
  const [absencesFiche, setAbsencesFiche] = useState([]);
  const [ajoutAbsence, setAjoutAbsence] = useState(null);   // null = formulaire replié
  const chargerAbsencesDe = useCallback((staffId) => {
    if (!staffId) { setAbsencesFiche([]); return; }
    api.get('/planning/absences', { params: { staff_id: staffId } })
      .then(r => setAbsencesFiche(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAbsencesFiche([]));
  }, []);

  // Matin / Après-midi ne sont pas des constantes : les bornes se déduisent des
  // horaires de l'atelier du collaborateur, sinon « après-midi » voudrait dire
  // 14 h partout alors que les ateliers n'ouvrent pas tous à la même heure.
  const bornesPeriode = (a, staffId = editStaff?.id) => {
    if (!a.periode || a.periode === 'journee') return [null, null];
    if (a.periode === 'creneau') return [a.heure_debut || null, a.heure_fin || null];
    const membre = (staffList || []).find(x => String(x.id) === String(staffId));
    const at = (ateliersList || []).find(x => String(x.id) === String(membre?.atelier_id));
    const ouv = (at?.heure_ouverture || '07:00').slice(0, 5);
    const fer = (at?.heure_fermeture || '17:00').slice(0, 5);
    // Bascule à la mi-journée, arrondie au quart d'heure inférieur.
    const mn = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const pivot = Math.floor((mn(ouv) + mn(fer)) / 2 / 15) * 15;
    const hhmm = (v) => `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
    return a.periode === 'matin' ? [ouv, hhmm(pivot)] : [hhmm(pivot), fer];
  };

  const enregistrerAbsenceFiche = async () => {
    if (!ajoutAbsence?.date_debut) { alert('Indiquez au moins une date de début.'); return; }
    try {
      const [hd, hf] = bornesPeriode(ajoutAbsence);
      const r = await api.post('/planning/absences', {
        staff_id: editStaff.id,
        date_debut: ajoutAbsence.date_debut,
        // Une absence d'un seul jour : la date de fin vaut celle de début.
        date_fin: ajoutAbsence.date_fin || ajoutAbsence.date_debut,
        motif: ajoutAbsence.motif,
        commentaire: ajoutAbsence.commentaire || null,
        heure_debut: hd, heure_fin: hf,
        recurrence: ajoutAbsence.repete
          ? { jours: ajoutAbsence.jours, jusqua: ajoutAbsence.jusqua }
          : undefined,
      });
      if (r.data?.occurrences) {
        alert(`${r.data.occurrences} absences créées, du ${formatDateFr(r.data.de)} au ${formatDateFr(r.data.a)}.`);
      }
      setAjoutAbsence(null);
      chargerAbsencesDe(editStaff.id);
      loadAbsences();
    } catch (e) { alert(e.response?.data?.error || 'Enregistrement impossible.'); }
  };

  const savePause = async () => {
    try {
      if (editPause) {
        await api.put(`/planning/pauses/${editPause.id}`, { ...pauseForm, concession_id: pauseForm.concession_id || null });
      } else {
        await api.post('/planning/pauses', { ...pauseForm, concession_id: pauseForm.concession_id || null });
      }
      setShowPauseForm(false);
      setEditPause(null);
      setPauseForm({ ...defaultPauseForm, concession_id: concessionId || '' });
      onReload();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  const deletePause = async (id) => {
    if (!window.confirm('Supprimer cette pause ?')) return;
    await api.delete(`/planning/pauses/${id}`);
    onReload();
  };

  const openEditPause = (p) => {
    setEditPause(p);
    setPauseForm({
      concession_id: p.concession_id || '',
      atelier_id: p.atelier_id || '',
      heure_debut: (p.heure_debut || '').substring(0, 5),
      heure_fin: (p.heure_fin || '').substring(0, 5),
      libelle: p.libelle || 'Pause',
      lundi: p.lundi, mardi: p.mardi, mercredi: p.mercredi, jeudi: p.jeudi, vendredi: p.vendredi, samedi: p.samedi,
    });
    setShowPauseForm(true);
  };

  const filteredPauses = concessionId ? (pausesList || []).filter(p => String(p.concession_id) === String(concessionId)) : (pausesList || []);

  const openEditStaff = (s) => {
    setEditStaff(s);
    setAjoutAbsence(null);
    chargerAbsencesDe(s.id);
    setStaffForm({ nom: s.nom, prenom: s.prenom, role: s.role, atelier_id: s.atelier_id || '', manager: s.manager || '', specialite: s.specialite || '', couleur: s.couleur || '#6366f1', concession_id: s.concession_id || '', nextlane_rcp_ids: s.nextlane_rcp_ids || {},
      actif: s.actif !== false, ordre: s.ordre ?? 0,
      date_entree: (s.date_entree || '').slice(0, 10), date_sortie: (s.date_sortie || '').slice(0, 10) });
    setShowStaffForm(true);
  };

  // Un onglet masqué ne peut pas rester actif : sinon le panneau s'ouvre sur un
  // contenu vide, sans que rien n'explique pourquoi.
  useEffect(() => {
    const autorises = ONGLETS_CONFIG.filter(([, , cap]) => peut(cap)).map(([c]) => c);
    if (autorises.length && !autorises.includes(tab)) setTab(autorises[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, peut]);

  return (
    <div className="planning-config">
      <div className="config-toolbar">
        <select value={concessionId} onChange={e => setConcessionId(e.target.value)}>
          <option value="">Toutes concessions</option>
          {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <div className="config-tabs">
          {ONGLETS_CONFIG.filter(([, , cap]) => peut(cap)).map(([cle, libelle]) => (
            <button key={cle} className={tab === cle ? 'active' : ''} onClick={() => setTab(cle)}>{libelle}</button>
          ))}
        </div>
      </div>

      {tab === 'acces' && peut('config.gestion_acces') && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>🔐 Gestion des accès au Planning</h3>
          </div>
          <div className="acces-explication">
            <p><strong>🔧 Technicien</strong> — grille du jour en lecture, et trois gestes par clic droit :
              statut atelier (limité à <em>En intervention</em>, <em>Diag en cours</em>, <em>Diag terminé</em>),
              commentaire, position parking. Rien d'autre.</p>
            <p><strong>🗝️ Administrateur</strong> — accès complet au module (comportement historique).</p>
            <p><strong>— Aucun rôle</strong> — l'accès au Planning est <span className="acces-bloque-txt">bloqué</span>,
              même si la page lui est ouverte dans les Permissions : le choix d'un rôle est obligatoire.</p>
          </div>
          {accesErreur && <p className="acces-erreur">⚠️ {accesErreur}</p>}
          <table className="config-table">
            <thead>
              <tr><th>Collaborateur</th><th>Identifiant</th><th>Concession</th><th>Rôle Planning</th><th>État</th></tr>
            </thead>
            <tbody>
              {acces.map(a => (
                <tr key={a.user_id}>
                  <td>{[a.prenom, a.nom].filter(Boolean).join(' ') || a.username}</td>
                  <td>{a.username}</td>
                  <td>{a.concession_nom || '—'}</td>
                  <td>
                    <select
                      value={a.role_planning || ''}
                      onChange={(e) => changerRole(a.user_id, e.target.value || null)}
                    >
                      <option value="">— Aucun rôle —</option>
                      {rolesPlanning.map(r => (
                        <option key={r.code} value={r.code}>{r.libelle}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {a.role_planning
                      ? <span className="acces-badge acces-badge--ok">✓ {rolesPlanning.find(r => r.code === a.role_planning)?.libelle || a.role_planning}</span>
                      : <span className="acces-badge acces-badge--bloque">⚠️ Aucun rôle — accès bloqué</span>}
                  </td>
                </tr>
              ))}
              {acces.length === 0 && !accesErreur && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  Aucun compte n'a la page Planning dans ses permissions.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'staff' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>👷 Mécaniciens & Réceptionnaires</h3>
            <button className="btn-primary btn-sm" onClick={() => { setEditStaff(null); setStaffForm({ nom: '', prenom: '', role: 'mecanicien', atelier_id: '', manager: '', specialite: '', couleur: '#6366f1', concession_id: concessionId || '', nextlane_rcp_ids: {}, actif: true, ordre: 0, date_entree: '', date_sortie: '' }); setShowStaffForm(true); }}>+ Ajouter</button>
          </div>
          {showStaffForm && (
            <div className="modal-overlay">
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editStaff ? `${editStaff.prenom || ''} ${editStaff.nom}`.trim() : 'Nouveau collaborateur'}</h2>
                  <button className="modal-close" onClick={() => { setShowStaffForm(false); setEditStaff(null); }}>✕</button>
                </div>
                <div className="modal-body">

                  <div className="cfg-grille">
                    <label className="cfg-bloc">
                      <span>Prénom</span>
                      <input value={staffForm.prenom} onChange={e => setStaffForm(p => ({ ...p, prenom: e.target.value }))} />
                    </label>
                    <label className="cfg-bloc">
                      <span>Nom</span>
                      <input value={staffForm.nom} onChange={e => setStaffForm(p => ({ ...p, nom: e.target.value }))} />
                    </label>
                    <label className="cfg-bloc">
                      <span>Concession</span>
                      <select value={staffForm.concession_id} onChange={e => setStaffForm(p => ({ ...p, concession_id: e.target.value }))}>
                        <option value="">— Concession —</option>
                        {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                      </select>
                    </label>
                    <label className="cfg-bloc">
                      <span>Rôle</span>
                      <select value={staffForm.role} onChange={e => setStaffForm(p => ({ ...p, role: e.target.value }))}>
                        <option value="mecanicien">Mécanicien</option>
                        <option value="receptionnaire">Réceptionnaire</option>
                      </select>
                    </label>
                    <label className="cfg-bloc">
                      <span>Atelier</span>
                      <select value={staffForm.atelier_id} onChange={e => setStaffForm(p => ({ ...p, atelier_id: e.target.value }))}>
                        <option value="">— Aucun atelier —</option>
                        {(concessionId ? filteredAteliers : ateliersList).map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                      </select>
                    </label>
                    <label className="cfg-bloc">
                      <span>Manager direct</span>
                      <input value={staffForm.manager} onChange={e => setStaffForm(p => ({ ...p, manager: e.target.value }))} />
                    </label>
                    <label className="cfg-bloc cfg-bloc--large">
                      <span>Spécialité</span>
                      <input placeholder="ex : Diag Opel" value={staffForm.specialite} onChange={e => setStaffForm(p => ({ ...p, specialite: e.target.value }))} />
                    </label>
                  </div>

                  <div className="cfg-section-titre">Présence dans le planning</div>
                  <div className="cfg-grille">
                    <label className="cfg-bloc">
                      <span>Date d'entrée</span>
                      <input type="date" value={staffForm.date_entree}
                        onChange={e => setStaffForm(p => ({ ...p, date_entree: e.target.value }))} />
                      <small>Premier jour travaillé. Vide si sans objet.</small>
                    </label>
                    <label className="cfg-bloc">
                      <span>Date de sortie</span>
                      <input type="date" value={staffForm.date_sortie}
                        onChange={e => setStaffForm(p => ({ ...p, date_sortie: e.target.value }))} />
                      <small>Fin de contrat, départ. Au-delà, la colonne disparaît de la grille.</small>
                    </label>
                    <label className="cfg-bloc">
                      <span>Ordre d'affichage</span>
                      <input type="number" min="0" value={staffForm.ordre}
                        onChange={e => setStaffForm(p => ({ ...p, ordre: e.target.value }))} />
                      <small>Position de la colonne dans la grille.</small>
                    </label>
                    <label className="cfg-bloc">
                      <span>Couleur</span>
                      <input type="color" value={staffForm.couleur}
                        onChange={e => setStaffForm(p => ({ ...p, couleur: e.target.value }))} />
                      <small>Repère visuel du collaborateur.</small>
                    </label>
                  </div>

                  <label className="cfg-bloc cfg-bloc--case">
                    <input type="checkbox" checked={!!staffForm.actif}
                      onChange={e => setStaffForm(p => ({ ...p, actif: e.target.checked }))} />
                    <span>
                      Actif
                      <small>Décoché, le collaborateur disparaît du planning — son historique est conservé. C'est la seule sortie propre : le supprimer orphelinerait ses interventions passées.</small>
                    </span>
                  </label>

                  {/* Absences : uniquement en MODIFICATION — à la création le
                      collaborateur n'a pas encore d'identifiant à rattacher. */}
                  {editStaff && (
                    <>
                      <div className="cfg-section-titre">
                        Absences
                        <button type="button" className="cfg-ajout"
                          onClick={() => setAjoutAbsence(ajoutAbsence ? null : { date_debut: '', date_fin: '', motif: 'conges', commentaire: '', periode: 'journee', heure_debut: '', heure_fin: '', repete: false, jours: [], jusqua: '' })}>
                          {ajoutAbsence ? 'Annuler' : '+ Ajouter'}
                        </button>
                      </div>

                      {ajoutAbsence && (
                        <div className="cfg-abs-ajout">
                          <ChampsAbsence valeur={ajoutAbsence} onChange={setAjoutAbsence} avecRecurrence />
                          <button className="btn-primary btn-sm cfg-abs-valider" onClick={enregistrerAbsenceFiche}>
                            Enregistrer l'absence
                          </button>
                        </div>
                      )}

                      {absencesFiche.length === 0 && !ajoutAbsence && (
                        <p className="cfg-abs-vide">Aucune absence enregistrée pour ce collaborateur.</p>
                      )}
                      {absencesFiche.length > 0 && (
                        <ul className="cfg-abs-liste">
                          {absencesFiche.map(a => {
                            const unSeulJour = !a.date_fin || formatDateFr(a.date_fin) === formatDateFr(a.date_debut);
                            return (
                              <li key={a.id} className="cfg-abs-cliquable" onClick={() => ouvrirAbsence(a)}
                                  title="Modifier cette absence">
                                <span className="cfg-abs-dates">
                                  {unSeulJour
                                    ? formatDateFr(a.date_debut)
                                    : <>{formatDateFr(a.date_debut)} <span className="cfg-abs-fleche">→</span> {formatDateFr(a.date_fin)}</>}
                                </span>
                                <span className="cfg-abs-motif">
                                  {MOTIFS.find(m => m.value === a.motif)?.label || a.motif}
                                  {a.heure_debut && <em className="cfg-abs-heures">{String(a.heure_debut).slice(0, 5)}–{String(a.heure_fin).slice(0, 5)}</em>}
                                  {a.serie_id && <em className="cfg-abs-serie" title="Fait partie d'une série récurrente">série</em>}
                                </span>
                                <span className="cfg-abs-com">{a.commentaire || ''}</span>
                                <button className="btn-icon delete" title="Supprimer cette absence"
                                  onClick={e => { e.stopPropagation(); deleteAbsence(a.id); }}>🗑️</button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  )}

                  {staffForm.role === 'receptionnaire' && (
                    <>
                      <div className="cfg-section-titre">Correspondance Datacar</div>
                      <div className="cfg-datacar">
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4 }}>
                    Sans correspondance, le rendez-vous poussé dans Datacar arrive sans réceptionnaire et doit être ressaisi à la main.
                  </div>
                  {rcpLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Chargement des codes Datacar…</div>}
                  {rcpErreur && <div style={{ fontSize: 12, color: 'var(--color-warning)' }}>{rcpErreur}</div>}
                  {!staffForm.concession_id && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Choisissez d'abord une concession.</div>
                  )}
                  {!rcpLoading && staffForm.concession_id && rcpDealerships.length === 0 && !rcpErreur && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Aucun DMS Datacar pour cette concession.</div>
                  )}
                  {rcpDealerships.map(d => (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12.5, minWidth: 170 }}>{d.name}</span>
                      <select
                        style={{ flex: 1, maxWidth: 380 }}
                        value={staffForm.nextlane_rcp_ids?.[d.id] || ''}
                        onChange={e => {
                          const code = e.target.value;
                          setStaffForm(p => {
                            const suivant = { ...(p.nextlane_rcp_ids || {}) };
                            if (code) suivant[d.id] = code; else delete suivant[d.id];
                            return { ...p, nextlane_rcp_ids: suivant };
                          });
                        }}
                      >
                        <option value="">— Non mappé —</option>
                        {(rcpOptions[d.id] || []).map(o => (
                          <option key={o.id} value={o.id}>{o.id} — {o.description}</option>
                        ))}
                        {/* Code déjà enregistré mais absent de la liste (référentiel Datacar
                            modifié, ou API injoignable) : on le garde visible plutôt que de
                            l'effacer en silence au premier enregistrement. */}
                        {staffForm.nextlane_rcp_ids?.[d.id]
                          && !(rcpOptions[d.id] || []).some(o => String(o.id) === String(staffForm.nextlane_rcp_ids[d.id])) && (
                          <option value={staffForm.nextlane_rcp_ids[d.id]}>
                            {staffForm.nextlane_rcp_ids[d.id]} — code enregistré, absent du référentiel
                          </option>
                        )}
                      </select>
                    </div>
                  ))}
                      </div>
                    </>
                  )}

                </div>
                <div className="modal-footer">
                  <button className="btn-secondary" onClick={() => { setShowStaffForm(false); setEditStaff(null); }}>Annuler</button>
                  <button className="btn-primary" onClick={saveStaff}>{editStaff ? 'Enregistrer' : 'Ajouter'}</button>
                </div>
              </div>
            </div>
          )}
          <div className="table-container">
            <table className="config-table">
              <thead><tr><th></th><th>Identité</th><th>Rôle</th><th>Spécialité</th><th>Atelier</th><th>Manager</th><th>Concession</th><th>Datacar</th><th></th></tr></thead>
              <tbody>
                {filteredStaff.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Aucun collaborateur configuré</td></tr>}
                {filteredStaff.map(s => (
                  <tr key={s.id} className="cfg-ligne-cliquable" onClick={() => openEditStaff(s)}
                      title="Ouvrir la fiche du collaborateur">
                    <td><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: s.couleur || '#6366f1' }} /></td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {s.nom}
                        {s.actif === false && <span className="status-error" style={{ marginLeft: 8, fontSize: 11 }}>Inactif</span>}
                        {s.actif !== false && s.date_sortie && <span className="status-warning" style={{ marginLeft: 8, fontSize: 11 }} title={`Sortie le ${formatDateFr(s.date_sortie)}`}>Sortie {formatDateFr(s.date_sortie)}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.prenom}</div>
                    </td>
                    <td><span className={`config-badge ${s.role === 'mecanicien' ? 'badge-meca' : 'badge-recep'}`}>{s.role === 'mecanicien' ? '🔧 Mécanicien' : '🚗 Réceptionnaire'}</span></td>
                    <td>{s.specialite || '—'}</td>
                    <td>{s.atelier_nom || '—'}</td>
                    <td>{s.manager || '—'}</td>
                    <td>{s.concession_nom || '—'}</td>
                    <td>
                      {s.role !== 'receptionnaire'
                        ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                        : Object.keys(s.nextlane_rcp_ids || {}).length > 0
                          ? <span className="status-success" title={Object.entries(s.nextlane_rcp_ids).map(([k, v]) => `${k} → ${v}`).join('\n')}>
                              {Object.values(s.nextlane_rcp_ids).join(' / ')}
                            </span>
                          : <span className="status-warning" title="Les RDV poussés dans Datacar arriveront sans réceptionnaire">Non mappé</span>}
                    </td>
                    {/* stopPropagation : sans lui, la suppression ouvrirait aussi la
                        fiche puisque la ligne entière est cliquable. */}
                    <td onClick={e => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button className="btn-icon" onClick={() => openEditStaff(s)} title="Modifier">✏️</button>
                        <button className="btn-icon delete" onClick={() => deleteStaff(s.id)} title="Supprimer">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'ateliers' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>🏭 Ateliers</h3>
            <button className="btn-primary btn-sm" onClick={() => { setEditAtelierId(null); setAtelierForm({ nom: '', concession_id: concessionId || '', heure_ouverture: '07:00', heure_fermeture: '17:00', jours_ouverture: [1, 2, 3, 4, 5, 6], ordre: '' }); setShowAtelierForm(true); }}>+ Ajouter</button>
          </div>
          {showAtelierForm && (
            <div className="config-form" style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
              <select value={atelierForm.concession_id} onChange={e => setAtelierForm(p => ({ ...p, concession_id: e.target.value }))} disabled={!!editAtelierId}>
                <option value="">— Concession —</option>
                {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
              <input placeholder="Nom de l'atelier (ex: Atelier Mécanique, Atelier Rapide…)" value={atelierForm.nom} onChange={e => setAtelierForm(p => ({ ...p, nom: e.target.value }))} style={{ flex: 1, minWidth: 180 }} />
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', gap: 2 }}>
                Ouverture
                <input type="time" step="900" value={atelierForm.heure_ouverture} onChange={e => setAtelierForm(p => ({ ...p, heure_ouverture: e.target.value }))} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', gap: 2 }}>
                Fermeture
                <input type="time" step="900" value={atelierForm.heure_fermeture} onChange={e => setAtelierForm(p => ({ ...p, heure_fermeture: e.target.value }))} />
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Jours d'ouverture</span>
                <div className="config-jours">
                  {JOURS_ISO.map(j => {
                    const ouvert = atelierForm.jours_ouverture.includes(j.iso);
                    return (
                      <button
                        type="button"
                        key={j.iso}
                        className={`config-jour${ouvert ? ' is-ouvert' : ''}`}
                        title={`${j.long} — ${ouvert ? 'ouvert' : 'fermé'}`}
                        aria-pressed={ouvert}
                        onClick={() => setAtelierForm(p => ({
                          ...p,
                          jours_ouverture: ouvert
                            ? p.jours_ouverture.filter(v => v !== j.iso)
                            : [...p.jours_ouverture, j.iso].sort((a, b) => a - b),
                        }))}
                      >
                        {j.court}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Position dans la barre du planning. Laisse vide a la creation,
                  le serveur range l'atelier a la suite des autres. */}
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', gap: 2 }}>
                Position
                <input
                  type="number"
                  min="0"
                  style={{ width: 80 }}
                  value={atelierForm.ordre}
                  placeholder="auto"
                  title="Ordre d'affichage dans la barre du planning (le plus petit en premier)"
                  onChange={e => setAtelierForm(p => ({ ...p, ordre: e.target.value }))}
                />
              </label>
              <button className="btn-primary btn-sm" onClick={saveAtelier} disabled={atelierForm.jours_ouverture.length === 0}>{editAtelierId ? 'Enregistrer' : 'Ajouter'}</button>
              <button className="btn-secondary btn-sm" onClick={() => { setShowAtelierForm(false); setEditAtelierId(null); }}>Annuler</button>
            </div>
          )}
          <div className="table-container">
            <table className="config-table">
              <thead><tr><th>Nom</th><th>Concession</th><th>Horaires</th><th>Jours</th><th>Position</th><th>Collaborateurs</th><th></th></tr></thead>
              <tbody>
                {filteredAteliers.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Aucun atelier configuré</td></tr>}
                {filteredAteliers.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600 }}>{a.nom}</td>
                    <td>{concessions.find(c => c.id === a.concession_id)?.nom || '—'}</td>
                    <td>{(a.heure_ouverture || '07:00').slice(0, 5)} – {(a.heure_fermeture || '17:00').slice(0, 5)}</td>
                    <td>
                      {/* Les jours fermés restent affichés en grisé : lire « L M M J V S̶ D̶ »
                          dit d'un coup d'œil ce qui est ouvert ET ce qui ne l'est pas, là où
                          une liste des seuls jours ouverts oblige à reconstituer le reste. */}
                      <span className="config-jours config-jours--lecture">
                        {JOURS_ISO.map(j => {
                          const ouverts = (a.jours_ouverture && a.jours_ouverture.length ? a.jours_ouverture.map(Number) : [1, 2, 3, 4, 5, 6]);
                          const ouvert = ouverts.includes(j.iso);
                          return <span key={j.iso} className={`config-jour${ouvert ? ' is-ouvert' : ''}`} title={`${j.long} — ${ouvert ? 'ouvert' : 'fermé'}`}>{j.court}</span>;
                        })}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{a.ordre ?? '—'}</td>
                    <td>{staffList.filter(s => s.atelier_id === a.id).map(s => s.prenom).join(', ') || '—'}</td>
                    <td>
                      <button className="btn-icon" onClick={() => editAtelier(a)} title="Modifier">✏️</button>
                      <button className="btn-icon" onClick={() => deleteAtelier(a.id)} title="Supprimer">🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'pauses' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>☕ Pauses</h3>
            <button className="btn-primary btn-sm" onClick={() => { setEditPause(null); setPauseForm({ ...defaultPauseForm, concession_id: concessionId || '' }); setShowPauseForm(true); }}>+ Ajouter</button>
          </div>
          {showPauseForm && (
            <div className="config-form" style={{ flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={pauseForm.concession_id} onChange={e => setPauseForm(p => ({ ...p, concession_id: e.target.value }))}>
                  <option value="">— Concession —</option>
                  {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
                {/* Atelier vide = pause de TOUTE la concession (comportement historique).
                    La liste suit la concession choisie dans le formulaire. */}
                <select value={pauseForm.atelier_id}
                  onChange={e => setPauseForm(p => ({ ...p, atelier_id: e.target.value }))}
                  title="Laisser « Tous les ateliers » pour une pause commune à la concession">
                  <option value="">— Tous les ateliers —</option>
                  {(ateliersList || [])
                    .filter(a => !pauseForm.concession_id || String(a.concession_id) === String(pauseForm.concession_id))
                    .map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                </select>
                <input placeholder="Libellé (ex: Pause déjeuner)" value={pauseForm.libelle} onChange={e => setPauseForm(p => ({ ...p, libelle: e.target.value }))} style={{ flex: 1, minWidth: 180 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ fontSize: '0.82rem', color: '#6b7280' }}>De</label>
                  <input type="time" value={pauseForm.heure_debut} onChange={e => setPauseForm(p => ({ ...p, heure_debut: e.target.value }))} />
                  <label style={{ fontSize: '0.82rem', color: '#6b7280' }}>à</label>
                  <input type="time" value={pauseForm.heure_fin} onChange={e => setPauseForm(p => ({ ...p, heure_fin: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.82rem', color: '#6b7280', fontWeight: 600 }}>Jours :</span>
                {JOURS_SEMAINE.map(j => (
                  <label key={j.key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={pauseForm[j.key]}
                      onChange={e => setPauseForm(p => ({ ...p, [j.key]: e.target.checked }))}
                      style={{ accentColor: '#6366f1' }}
                    />
                    {j.label}
                  </label>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button className="btn-primary btn-sm" onClick={savePause}>{editPause ? 'Modifier' : 'Ajouter'}</button>
                  <button className="btn-secondary btn-sm" onClick={() => { setShowPauseForm(false); setEditPause(null); }}>Annuler</button>
                </div>
              </div>
            </div>
          )}
          <div className="table-container">
            <table className="config-table">
              <thead>
                <tr><th>Libellé</th><th>Horaires</th><th>Lun</th><th>Mar</th><th>Mer</th><th>Jeu</th><th>Ven</th><th>Sam</th><th>Concession</th><th>Atelier</th><th></th></tr>
              </thead>
              <tbody>
                {filteredPauses.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Aucune pause configurée</td></tr>}
                {filteredPauses.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.libelle || 'Pause'}</td>
                    <td>{(p.heure_debut || '').substring(0, 5)} – {(p.heure_fin || '').substring(0, 5)}</td>
                    <td style={{ textAlign: 'center' }}>{p.lundi ? '✅' : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{p.mardi ? '✅' : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{p.mercredi ? '✅' : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{p.jeudi ? '✅' : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{p.vendredi ? '✅' : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{p.samedi ? '✅' : '—'}</td>
                    <td>{p.concession_nom || '—'}</td>
                    <td>{p.atelier_nom || <span style={{ color: '#9ca3af' }}>Tous</span>}</td>
                    <td>
                      <button className="btn-icon" onClick={() => openEditPause(p)} title="Modifier">✏️</button>
                      <button className="btn-icon" onClick={() => deletePause(p.id)} title="Supprimer">🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'absences' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>📅 Congés & Absences</h3>
            <button className="btn-primary btn-sm" onClick={() => setAbsenceEditee({
              id: null, staff_id: '', date_debut: '', date_fin: '', motif: 'conges',
              commentaire: '', periode: 'journee', heure_debut: '', heure_fin: '',
              repete: false, jours: [], jusqua: '',
            })}>+ Ajouter</button>
          </div>
          <div className="table-container">
            <table className="config-table">
              <thead><tr><th>Collaborateur</th><th>Du</th><th>Au</th><th>Horaire</th><th>Motif</th><th>Commentaire</th><th></th></tr></thead>
              <tbody>
                {filteredAbsences.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>Aucune absence enregistrée</td></tr>}
                {filteredAbsences.map(a => (
                  <tr key={a.id} className="cfg-ligne-cliquable" onClick={() => ouvrirAbsence(a)}
                      title="Modifier cette absence">
                    <td style={{ fontWeight: 600 }}>{a.staff_nom}</td>
                    <td>{formatDateFr(a.date_debut)}</td>
                    <td>{formatDateFr(a.date_fin)}</td>
                    <td>{a.heure_debut
                      ? `${a.heure_debut.slice(0, 5)} – ${(a.heure_fin || '').slice(0, 5)}`
                      : 'Journée'}</td>
                    <td>
                      {MOTIFS.find(m => m.value === a.motif)?.label || a.motif}
                      {a.serie_id && <em className="cfg-abs-serie" title="Fait partie d'une série récurrente">série</em>}
                    </td>
                    <td>{a.commentaire || '—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button className="btn-icon" onClick={() => ouvrirAbsence(a)} title="Modifier">✏️</button>
                        <button className="btn-icon delete" onClick={() => deleteAbsence(a.id)} title="Supprimer">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {tab === 'prestations' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>🌐 Prestations disponibles en ligne</h3>
            <button className="btn-primary btn-sm" onClick={() => {
              setEditPrest(null);
              setPrestForm({ role_rapide: 'none', role_meca: 'none', categorie: '', libelle: '', duree: 1, icone: '', visible_planning: true, concession_id: concessionId || '' });
              setShowPrestForm(true);
            }}>+ Ajouter</button>
          </div>

          {showPrestForm && (
            <div className="modal-overlay">
              <div className="modal-content small" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editPrest ? 'Modifier la prestation' : 'Nouvelle prestation'}</h2>
                  <button className="modal-close" onClick={() => { setShowPrestForm(false); setEditPrest(null); }}>✕</button>
                </div>
                <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', background: 'var(--color-info-light)', padding: '10px', borderRadius: '8px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>⚡ Service Rapide</label>
                    <select className="page-select" value={prestForm.role_rapide} onChange={e => setPrestForm({ ...prestForm, role_rapide: e.target.value })} style={{ width: '100%' }}>
                      <option value="none">Non proposé</option>
                      <option value="principale">Principale</option>
                      <option value="complement">Complément</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>🔧 Atelier Mécanique</label>
                    <select className="page-select" value={prestForm.role_meca} onChange={e => setPrestForm({ ...prestForm, role_meca: e.target.value })} style={{ width: '100%' }}>
                      <option value="none">Non proposé</option>
                      <option value="principale">Principale</option>
                      <option value="complement">Complément</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Catégorie</label>
                  <input className="page-search" placeholder="ex: revision, freinage" value={prestForm.categorie} onChange={e => setPrestForm({ ...prestForm, categorie: e.target.value })} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Libellé</label>
                  <input className="page-search" placeholder="ex: Révision 10 000km" value={prestForm.libelle} onChange={e => setPrestForm({ ...prestForm, libelle: e.target.value })} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Durée</label>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      className="page-search"
                      type="number"
                      min="0"
                      step="1"
                      value={Math.floor(prestForm.duree || 0)}
                      onChange={e => {
                        const h = Math.max(0, parseInt(e.target.value || '0', 10) || 0);
                        const m = Math.round(((prestForm.duree || 0) - Math.floor(prestForm.duree || 0)) * 60);
                        setPrestForm({ ...prestForm, duree: h + m / 60 });
                      }}
                      style={{ width: '70px' }}
                    />
                    <span style={{ fontSize: '13px' }}>h</span>
                    <input
                      className="page-search"
                      type="number"
                      min="0"
                      max="59"
                      step="5"
                      value={Math.round(((prestForm.duree || 0) - Math.floor(prestForm.duree || 0)) * 60)}
                      onChange={e => {
                        const m = Math.min(59, Math.max(0, parseInt(e.target.value || '0', 10) || 0));
                        const h = Math.floor(prestForm.duree || 0);
                        setPrestForm({ ...prestForm, duree: h + m / 60 });
                      }}
                      style={{ width: '70px' }}
                    />
                    <span style={{ fontSize: '13px' }}>min</span>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Icône (emoji)</label>
                  <input className="page-search" placeholder="🔧" value={prestForm.icone} onChange={e => setPrestForm({ ...prestForm, icone: e.target.value })} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Concession</label>
                  <select className="page-select" value={prestForm.concession_id} onChange={e => setPrestForm({ ...prestForm, concession_id: e.target.value })} style={{ width: '100%' }}>
                    <option value="">— Toutes —</option>
                    {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '10px', display: 'flex', gap: '24px' }}>
                <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={prestForm.visible_planning} onChange={e => setPrestForm({ ...prestForm, visible_planning: e.target.checked })} style={{ marginRight: '6px' }} />
                  Visible dans le planning interne
                </label>
              </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary btn-sm" onClick={() => { setShowPrestForm(false); setEditPrest(null); }}>Annuler</button>
                  <button className="btn-primary btn-sm" onClick={savePrest} disabled={!prestForm.libelle || !prestForm.categorie}>Enregistrer</button>
                </div>
              </div>
            </div>
          )}

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th>Libellé</th>
                  <th>Durée</th>
                  <th>⚡ Rapide</th>
                  <th>🔧 Méca</th>
                  <th>Planning</th>
                  <th>Concession</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {prestationsOnline.filter(p => p.actif !== false).map(p => (
                  <tr key={p.id}>
                    <td>{p.categorie}</td>
                    <td>{p.icone} {p.libelle}</td>
                    <td>{formatDureeHm(p.duree)}</td>
                    <td>{roleBadge(p.role_rapide)}</td>
                    <td>{roleBadge(p.role_meca)}</td>
                    <td>{p.visible_planning !== false ? '✓' : '—'}</td>
                    <td>{concessions.find(c => c.id === p.concession_id)?.nom || '—'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Modifier" onClick={() => {
                          setEditPrest(p);
                          setPrestForm({
                            role_rapide: p.role_rapide || 'none', role_meca: p.role_meca || 'none',
                            categorie: p.categorie, libelle: p.libelle,
                            duree: p.duree, icone: p.icone || '',
                            visible_planning: p.visible_planning !== false, concession_id: p.concession_id || ''
                          });
                          setShowPrestForm(true);
                        }}>✏️</button>
                        <button className="btn-icon delete" title="Désactiver" onClick={() => deletePrest(p.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'messages' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>✉️ Messages types (SMS et e-mail)</h3>
            <button className="btn-primary btn-sm" onClick={() => {
              setEditMsg(null);
              setMsgForm({ libelle: '', canal: 'sms', objet: '', contenu: '', ordre: (messages.length + 1), actif: true, concession_id: concessionId || '' });
              setShowMsgForm(true);
            }}>+ Ajouter</button>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
            Ces modèles s'affichent dans le Planning Atelier, sur <strong>clic droit d'une carte → Contacter le client</strong>.
            Ils sont propres à chaque concession. Variables remplacées à l'envoi :{' '}
            {(messageVars.length ? messageVars : ['CLIENT', 'IMMAT', 'OR', 'MARQUE', 'MODELE', 'DATE', 'HEURE', 'CONCESSION'])
              .map(v => <code key={v} style={{ marginRight: '6px' }}>{'{' + v + '}'}</code>)}
          </p>
          {/* Ces deux-là ne valent que pour le canal « atelier » : elles sont
              remplacées à l'INSERTION du texte dans le compte rendu, pas à
              l'envoi d'un message — c'est le moment de l'écriture qu'on
              horodate. */}
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
            Pour un <strong>🔧 texte d'atelier</strong>, deux variables de plus, remplacées
            au moment où le mécanicien insère le texte :{' '}
            <code style={{ marginRight: '6px' }}>{'{DATE_AJOUT}'}</code>
            <code style={{ marginRight: '6px' }}>{'{HEURE_AJOUT}'}</code>
          </p>

          {showMsgForm && (
            <div className="modal-overlay">
              <div className="modal-content small" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editMsg ? 'Modifier le message type' : 'Nouveau message type'}</h2>
                  <button className="modal-close" onClick={() => { setShowMsgForm(false); setEditMsg(null); }}>✕</button>
                </div>
                <div className="modal-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Libellé (ce que verra le réceptionnaire)</label>
                      <input className="page-search" placeholder="ex: Véhicule prêt" value={msgForm.libelle}
                        onChange={e => setMsgForm({ ...msgForm, libelle: e.target.value })} style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Canal</label>
                      <select className="page-select" value={msgForm.canal}
                        onChange={e => setMsgForm({ ...msgForm, canal: e.target.value })} style={{ width: '100%' }}>
                        <option value="sms">SMS</option>
                        <option value="email">E-mail</option>
                        <option value="les_deux">SMS et e-mail</option>
                        <option value="atelier">🔧 Texte d'atelier (compte rendu)</option>
                      </select>
                    </div>
                  </div>

                  {msgForm.canal !== 'sms' && msgForm.canal !== 'atelier' && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Objet de l'e-mail</label>
                      <input className="page-search" placeholder="ex: Votre véhicule est prêt" value={msgForm.objet}
                        onChange={e => setMsgForm({ ...msgForm, objet: e.target.value })} style={{ width: '100%' }} />
                    </div>
                  )}

                  <div style={{ marginTop: '10px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Message</label>
                    <textarea className="page-search" rows={4} style={{ width: '100%', resize: 'vertical' }}
                      placeholder="Cher client, votre vehicule {IMMAT} est pret. L'equipe {CONCESSION}"
                      value={msgForm.contenu} onChange={e => setMsgForm({ ...msgForm, contenu: e.target.value })} />
                    {msgForm.canal !== 'email' && msgForm.canal !== 'atelier' && (
                      <div style={{ fontSize: '12px', marginTop: '4px', color: msgForm.contenu.length > 160 ? 'var(--color-error)' : 'var(--text-muted)' }}>
                        {msgForm.contenu.length} / 160 caractères
                        {msgForm.contenu.length > 160 && ' — un SMS ne peut pas dépasser 160 caractères'}
                        <span style={{ marginLeft: '8px' }}>· les accents sont retirés à l'envoi du SMS</span>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: '10px', display: 'flex', gap: '24px', alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, marginRight: '6px' }}>Ordre</label>
                      <input className="page-search" type="number" min="0" style={{ width: '80px' }}
                        value={msgForm.ordre} onChange={e => setMsgForm({ ...msgForm, ordre: parseInt(e.target.value) || 0 })} />
                    </div>
                    <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={msgForm.actif}
                        onChange={e => setMsgForm({ ...msgForm, actif: e.target.checked })} style={{ marginRight: '6px' }} />
                      Actif
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary btn-sm" onClick={() => { setShowMsgForm(false); setEditMsg(null); }}>Annuler</button>
                  <button className="btn-primary btn-sm" onClick={saveMsg}
                    disabled={!msgForm.libelle || !msgForm.contenu || (msgForm.canal !== 'email' && msgForm.canal !== 'atelier' && msgForm.contenu.length > 160)}>Enregistrer</button>
                </div>
              </div>
            </div>
          )}

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Ordre</th>
                  <th>Libellé</th>
                  <th>Canal</th>
                  <th>Message</th>
                  <th>Actif</th>
                  <th>Concession</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Aucun message type pour cette concession.</td></tr>
                )}
                {messages.map(m => (
                  <tr key={m.id}>
                    <td>{m.ordre}</td>
                    <td><strong>{m.libelle}</strong></td>
                    <td>{m.canal === 'sms' ? '💬 SMS' : m.canal === 'email' ? '📧 E-mail' : m.canal === 'atelier' ? "🔧 Texte d'atelier" : '💬📧 Les deux'}</td>
                    <td style={{ maxWidth: '420px', whiteSpace: 'normal' }}>
                      {m.objet && <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Objet : {m.objet}</div>}
                      {m.contenu}
                    </td>
                    <td>{m.actif ? '✓' : '—'}</td>
                    <td>{concessions.find(c => c.id === m.concession_id)?.nom || '—'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Modifier" onClick={() => {
                          setEditMsg(m);
                          setMsgForm({
                            libelle: m.libelle, canal: m.canal || 'sms', objet: m.objet || '',
                            contenu: m.contenu, ordre: m.ordre || 0, actif: m.actif !== false,
                            concession_id: m.concession_id || ''
                          });
                          setShowMsgForm(true);
                        }}>✏️</button>
                        <button className="btn-icon delete" title="Supprimer" onClick={() => deleteMsg(m.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'documents' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>📄 Documents types (impression)</h3>
            <button className="btn-primary btn-sm" onClick={() => {
              setEditDoc(null); setDocApercuHtml(null);
              setDocForm({ libelle: '', contenu: '', ordre: (docs.length + 1), actif: true, concession_id: concessionId || '' });
              setShowDocForm(true);
            }}>+ Ajouter</button>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
            Documents imprimés depuis la modale d'intervention (bouton <strong>🖨️ Imprimer</strong>) et remis au client —
            confirmation de rendez-vous, par exemple. Le contenu est du HTML ; variables remplacées à l'impression :{' '}
            {docVars.map(v => <code key={v} style={{ marginRight: '6px' }}>{'{' + v + '}'}</code>)}
          </p>

          {showDocForm && (
            <div className="modal-overlay">
              <div className="modal-content large" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editDoc ? 'Modifier le document type' : 'Nouveau document type'}</h2>
                  <button className="modal-close" onClick={() => { setShowDocForm(false); setEditDoc(null); setDocApercuHtml(null); }}>✕</button>
                </div>
                <div className="modal-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 120px auto', gap: '10px', alignItems: 'end' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Libellé (ce que verra le réceptionnaire)</label>
                      <input className="page-search" placeholder="ex: Confirmation de rendez-vous" value={docForm.libelle}
                        onChange={e => setDocForm({ ...docForm, libelle: e.target.value })} style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Ordre</label>
                      <input className="page-search" type="number" min="0" style={{ width: '100%' }}
                        value={docForm.ordre} onChange={e => setDocForm({ ...docForm, ordre: parseInt(e.target.value) || 0 })} />
                    </div>
                    <label style={{ fontSize: '13px', cursor: 'pointer', paddingBottom: '8px' }}>
                      <input type="checkbox" checked={docForm.actif}
                        onChange={e => setDocForm({ ...docForm, actif: e.target.checked })} style={{ marginRight: '6px' }} />
                      Actif
                    </label>
                  </div>

                  {/* Éditeur + aperçu côte à côte : l'admin retouche un libellé,
                      clique Aperçu, et voit la feuille telle qu'elle sortira. */}
                  <div style={{ display: 'grid', gridTemplateColumns: docApercuHtml ? '1fr 1fr' : '1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                        Contenu (HTML avec variables)
                      </label>
                      <textarea className="page-search" rows={20}
                        style={{ width: '100%', resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: '12px', lineHeight: 1.5 }}
                        value={docForm.contenu} onChange={e => setDocForm({ ...docForm, contenu: e.target.value })} />
                    </div>
                    {docApercuHtml && (
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          Aperçu — sur le dossier le plus récent de la concession
                        </label>
                        <iframe title="Aperçu du document" srcDoc={docApercuHtml}
                          style={{ width: '100%', height: '460px', border: '1px solid var(--border-default)', borderRadius: '8px', background: '#fff' }} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary btn-sm" onClick={apercuDoc} disabled={!docForm.contenu}>👁️ Aperçu</button>
                  <button className="btn-secondary btn-sm" onClick={() => { setShowDocForm(false); setEditDoc(null); setDocApercuHtml(null); }}>Annuler</button>
                  <button className="btn-primary btn-sm" onClick={saveDoc} disabled={!docForm.libelle || !docForm.contenu}>Enregistrer</button>
                </div>
              </div>
            </div>
          )}

          <div className="table-container">
            <table>
              <thead>
                <tr><th>Ordre</th><th>Libellé</th><th>Actif</th><th>Concession</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {docs.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Aucun document type pour cette concession.</td></tr>
                )}
                {docs.map(d => (
                  <tr key={d.id}>
                    <td>{d.ordre}</td>
                    <td><strong>{d.libelle}</strong></td>
                    <td>{d.actif ? '✓' : '—'}</td>
                    <td>{concessions.find(c => c.id === d.concession_id)?.nom || '—'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Modifier" onClick={() => {
                          setEditDoc(d); setDocApercuHtml(null);
                          setDocForm({ libelle: d.libelle, contenu: d.contenu, ordre: d.ordre || 0, actif: d.actif !== false, concession_id: d.concession_id || '' });
                          setShowDocForm(true);
                        }}>✏️</button>
                        <button className="btn-icon delete" title="Supprimer" onClick={() => deleteDoc(d.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'tags' && (
        <div className="config-section">
          <div className="config-section-header">
            <h3>🔖 Tags d'intervention</h3>
            <button className="btn-primary btn-sm" onClick={() => {
              setEditTag(null); setTagErreur('');
              setTagForm({
                nom: '',
                couleur: COULEURS_TAG[tagsAffiches.length % COULEURS_TAG.length],
                ordre: tagsAffiches.length + 1,
                actif: true,
                concession_id: concessionId || '',
              });
              setShowTagForm(true);
            }}>+ Ajouter</button>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
            Les tags décrivent une <strong>contrainte d'organisation</strong> (« Attente pièces », « À rappeler », « VIP »),
            là où le statut atelier décrit l'avancement du dossier. Un dossier peut en porter <strong>plusieurs</strong>.
            Ils se posent dans le Planning Atelier sur <strong>clic droit d'une carte → Tags</strong>, et servent de filtre au-dessus de la grille.
            Chaque concession a les siens : tant qu'aucun tag n'est créé, rien n'apparaît dans le Planning.
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 16px' }}>
            ⚠️ N'y écrivez <strong>ni nom de client, ni appréciation sur une personne</strong> : un tag est un mot d'organisation, visible de toute l'équipe.
          </p>

          {showTagForm && (
            <div className="modal-overlay">
              <div className="modal-content small" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editTag ? 'Modifier le tag' : 'Nouveau tag'}</h2>
                  <button className="modal-close" onClick={() => { setShowTagForm(false); setEditTag(null); }}>✕</button>
                </div>
                <div className="modal-body">
                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Libellé (40 caractères maximum)</label>
                  <input className="page-search" style={{ width: '100%' }} maxLength={40} autoFocus
                    placeholder="ex : Attente pièces" value={tagForm.nom}
                    onChange={e => { setTagForm({ ...tagForm, nom: e.target.value }); setTagErreur(''); }}
                    onKeyDown={e => { if (e.key === 'Enter' && tagForm.nom.trim()) saveTag(); }} />

                  <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', margin: '12px 0 4px' }}>Couleur</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {COULEURS_TAG.map(c => (
                      <button key={c} type="button" title={c}
                        onClick={() => setTagForm({ ...tagForm, couleur: c })}
                        style={{
                          width: 26, height: 26, borderRadius: '50%', background: c, cursor: 'pointer',
                          border: tagForm.couleur.toLowerCase() === c.toLowerCase()
                            ? '3px solid var(--text-primary)' : '1px solid var(--border-default)',
                        }} />
                    ))}
                    <input type="color" value={tagForm.couleur} title="Autre couleur"
                      onChange={e => setTagForm({ ...tagForm, couleur: e.target.value })}
                      style={{ width: 40, height: 28, padding: 0, border: '1px solid var(--border-default)', background: 'none', cursor: 'pointer' }} />
                  </div>

                  {/* Aperçu : ce que l'équipe verra réellement dans la barre de
                      filtre. Choisir une couleur à l'aveugle mène droit au tag
                      illisible qu'il faut refaire. */}
                  <div style={{ marginTop: '14px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Aperçu</label>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                      background: tagForm.couleur, color: getContrastColor(tagForm.couleur),
                    }}>{tagForm.nom.trim() || 'Libellé du tag'}</span>
                  </div>

                  <div style={{ marginTop: '14px', display: 'flex', gap: '24px', alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, marginRight: '6px' }}>Ordre</label>
                      <input className="page-search" type="number" min="0" style={{ width: '80px' }}
                        value={tagForm.ordre} onChange={e => setTagForm({ ...tagForm, ordre: parseInt(e.target.value) || 0 })} />
                    </div>
                    <label style={{ fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={tagForm.actif}
                        onChange={e => setTagForm({ ...tagForm, actif: e.target.checked })} style={{ marginRight: '6px' }} />
                      Actif
                    </label>
                  </div>

                  {tagErreur && (
                    <p style={{ color: 'var(--color-error)', fontSize: '13px', marginTop: '12px' }}>❌ {tagErreur}</p>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary btn-sm" onClick={() => { setShowTagForm(false); setEditTag(null); }}>Annuler</button>
                  <button className="btn-primary btn-sm" onClick={saveTag} disabled={!tagForm.nom.trim()}>Enregistrer</button>
                </div>
              </div>
            </div>
          )}

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Ordre</th>
                  <th>Tag</th>
                  <th>Dossiers</th>
                  <th>Actif</th>
                  <th>Concession</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tagsAffiches.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    Aucun tag pour cette concession — le Planning Atelier n'en proposera aucun.
                  </td></tr>
                )}
                {tagsAffiches.map(t => (
                  <tr key={t.id} style={{ opacity: t.actif ? 1 : 0.55 }}>
                    <td>{t.ordre}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                        background: t.couleur, color: getContrastColor(t.couleur),
                      }}>{t.nom}</span>
                    </td>
                    <td>{t.nb_dossiers > 0 ? t.nb_dossiers : '—'}</td>
                    <td>{t.actif ? '✓' : '—'}</td>
                    <td>{concessions.find(c => c.id === t.concession_id)?.nom || '—'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Modifier" onClick={() => {
                          setEditTag(t); setTagErreur('');
                          setTagForm({
                            nom: t.nom, couleur: t.couleur, ordre: t.ordre || 0,
                            actif: t.actif !== false, concession_id: t.concession_id || '',
                          });
                          setShowTagForm(true);
                        }}>✏️</button>
                        <button className={`btn-icon${t.actif ? ' delete' : ''}`}
                          title={t.actif ? 'Désactiver' : 'Réactiver'}
                          onClick={() => basculerTagActif(t)}>{t.actif ? '🗑️' : '↩️'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {absenceEditee && (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{absenceEditee.id ? (absenceEditee.staff_nom || 'Absence') : 'Nouvelle absence'}</h2>
                <button className="modal-close" onClick={() => setAbsenceEditee(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="cfg-grille">
                  {!absenceEditee.id && (
                    <label className="cfg-bloc">
                      <span>Collaborateur</span>
                      <select value={absenceEditee.staff_id || ''}
                        onChange={e => setAbsenceEditee(a => ({ ...a, staff_id: e.target.value }))}>
                        <option value="">— Choisir —</option>
                        {(concessionId ? filteredStaff : staffList).map(m => (
                          <option key={m.id} value={m.id}>{m.prenom} {m.nom}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  <ChampsAbsence valeur={absenceEditee} onChange={setAbsenceEditee}
                    avecRecurrence={!absenceEditee.id} />
                </div>
              </div>
              <div className="modal-footer">
                {absenceEditee.serie_id && (
                  <button className="btn-danger btn-sm cfg-abs-serie-suppr"
                    onClick={() => supprimerSerie(absenceEditee.serie_id)}>
                    Supprimer toute la série
                  </button>
                )}
                <button className="btn-secondary" onClick={() => setAbsenceEditee(null)}>Annuler</button>
                <button className="btn-primary" onClick={enregistrerAbsenceEditee}>Enregistrer</button>
              </div>
            </div>
          </div>
        )}

    </div>
  );
}

export default PlanningConfig;
